'use client';

/**
 * # Email Suggestion Banner
 * Displays AI-powered suggestions for email actions
 * Shows in inbox views with accept/decline options
 */

import React, { useState, useEffect } from 'react';
import {
  Lightbulb,
  Check,
  X,
  Archive,
  MessageSquare,
  UserPlus,
  Zap,
  AlertTriangle,
  Loader2,
  Clock,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Card } from '@/shared/components/ui/card';
import { EmailSuggestion } from '@/shared/types/email-suggestions';
import { emailSuggestionEngine } from '@/shared/lib/email-suggestion-engine';
import { logDev } from '@/shared/lib/log-dev';

interface EmailSuggestionBannerProps {
  emailId: string;
  threadId?: string;
  context?: {
    fromEmail: string;
    fromName?: string;
    subject: string;
    bodyText: string;
    receivedAt: Date;
    isRead: boolean;
    hasAttachments: boolean;
  };
  onSuggestionAccepted?: (suggestion: EmailSuggestion) => void;
  onSuggestionDeclined?: (suggestion: EmailSuggestion) => void;
  className?: string;
}

export function EmailSuggestionBanner({
  emailId,
  threadId,
  context,
  onSuggestionAccepted,
  onSuggestionDeclined,
  className = '',
}: EmailSuggestionBannerProps) {
  const [suggestions, setSuggestions] = useState<EmailSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    loadSuggestions();
  }, [emailId, context]);

  const loadSuggestions = async () => {
    if (!context) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const emailContext = {
        emailId,
        threadId: threadId || emailId,
        fromEmail: context.fromEmail,
        fromName: context.fromName,
        subject: context.subject,
        bodyText: context.bodyText,
        receivedAt: context.receivedAt,
        isRead: context.isRead,
        hasAttachments: context.hasAttachments,
      };

      const newSuggestions = await emailSuggestionEngine.analyzeEmail(emailContext);
      setSuggestions(newSuggestions);
    } catch (error) {
      logDev.error('[EmailSuggestionBanner] Error loading suggestions', error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (suggestion: EmailSuggestion) => {
    try {
      setProcessing(suggestion.id);
      await emailSuggestionEngine.acceptSuggestion(emailId, suggestion.id);
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      onSuggestionAccepted?.(suggestion);
    } catch (error) {
      logDev.error('[EmailSuggestionBanner] Error accepting suggestion', error);
    } finally {
      setProcessing(null);
    }
  };

  const handleDecline = async (suggestion: EmailSuggestion) => {
    try {
      await emailSuggestionEngine.declineSuggestion(emailId, suggestion.id);
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
      onSuggestionDeclined?.(suggestion);
    } catch (error) {
      logDev.error('[EmailSuggestionBanner] Error declining suggestion', error);
    }
  };

  const handleDismiss = async (suggestion: EmailSuggestion) => {
    try {
      await emailSuggestionEngine.dismissSuggestion(emailId, suggestion.id);
      setSuggestions(prev => prev.filter(s => s.id !== suggestion.id));
    } catch (error) {
      logDev.error('[EmailSuggestionBanner] Error dismissing suggestion', error);
    }
  };

  const getSuggestionIcon = (type: EmailSuggestion['type']) => {
    switch (type) {
      case 'archive':
        return <Archive className="h-4 w-4" />;
      case 'respond':
        return <MessageSquare className="h-4 w-4" />;
      case 'lead_creation':
        return <UserPlus className="h-4 w-4" />;
      case 'auto_response':
        return <Zap className="h-4 w-4" />;
      case 'priority_change':
        return <AlertTriangle className="h-4 w-4" />;
      case 'follow_up':
        return <Clock className="h-4 w-4" />;
      default:
        return <Lightbulb className="h-4 w-4" />;
    }
  };

  const getPriorityColor = (priority: EmailSuggestion['priority']) => {
    switch (priority) {
      case 'high':
        return 'border-red-200 bg-red-50 dark:bg-red-950/30';
      case 'medium':
        return 'border-yellow-200 bg-yellow-50 dark:bg-yellow-950/30';
      case 'low':
        return 'border-blue-200 bg-blue-50 dark:bg-blue-950/30';
      default:
        return 'border-gray-200 bg-gray-50 dark:bg-gray-900/50';
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-2 px-4 border border-gray-200 rounded-lg ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin text-gray-400 mr-2" />
        <span className="text-sm text-gray-500">Analyzing email...</span>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return null;
  }

  const topSuggestion = suggestions[0];
  const hasMultiple = suggestions.length > 1;

  return (
    <Card className={`border-2 ${getPriorityColor(topSuggestion.priority)} ${className}`}>
      <div className="p-3">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 mt-0.5">
              {getSuggestionIcon(topSuggestion.type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                  {topSuggestion.title}
                </h4>
                <span className="text-xs px-2 py-0.5 bg-white/60 dark:bg-gray-800/60 rounded-full text-gray-600 dark:text-gray-400">
                  {Math.round(topSuggestion.confidence)}% confidence
                </span>
                {topSuggestion.priority === 'high' && (
                  <span className="text-xs px-1.5 py-0.5 bg-red-100 dark:bg-red-900/50 text-red-700 dark:text-red-300 rounded">
                    High Priority
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {topSuggestion.description}
              </p>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleAccept(topSuggestion)}
              disabled={processing === topSuggestion.id}
              className="h-8 px-3 text-xs border-green-300 text-green-700 hover:bg-green-50 dark:border-green-700 dark:text-green-300 dark:hover:bg-green-950/30"
            >
              {processing === topSuggestion.id ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Check className="h-3 w-3 mr-1" />
              )}
              {topSuggestion.action.label}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => handleDecline(topSuggestion)}
              disabled={processing === topSuggestion.id}
              className="h-8 w-8 p-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Multiple suggestions indicator */}
        {hasMultiple && (
          <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              {expanded ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
              {suggestions.length - 1} more suggestion{suggestions.length - 1 > 1 ? 's' : ''}
            </button>

            {expanded && (
              <div className="mt-2 space-y-2">
                {suggestions.slice(1).map((suggestion) => (
                  <div
                    key={suggestion.id}
                    className="flex items-center justify-between p-2 bg-white/50 dark:bg-gray-800/50 rounded border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                      {getSuggestionIcon(suggestion.type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                          {suggestion.title}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                          {suggestion.description}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAccept(suggestion)}
                        disabled={processing === suggestion.id}
                        className="h-6 px-2 text-xs"
                      >
                        {processing === suggestion.id ? (
                          <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        ) : (
                          <Check className="h-2.5 w-2.5" />
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDecline(suggestion)}
                        disabled={processing === suggestion.id}
                        className="h-6 w-6 p-0"
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Dismiss option */}
        <div className="mt-2 flex justify-end">
          <button
            onClick={() => handleDismiss(topSuggestion)}
            className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            Don't show this again
          </button>
        </div>
      </div>
    </Card>
  );
}






